import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import startCase from 'lodash.startcase';
import { rem } from '@gbi/ui';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import RowItemElement from '../Buy/RowItem';
import SelectVaultLocation from '../Buy/SelectVaultLocation';
import SelectStorageType from '../Buy/SelectStorageType';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import QuantityStepper from './QuantityStepper';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import {
  deleteWizardKey,
  setWizardContent,
} from '../../util/helpers/wizardHelpers';
import {
  getVaultTierPrice,
  getTierForAmount,
} from '../../util/helpers/buyOrderHelpers';
import useProductPrice from '../../util/hooks/useProductPrice';
import useGetPeriodicPriceTiers from '../../util/hooks/useGetPeriodicPriceTiers';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACH_DEPOSIT_WIZARD,
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
  IRA_DEPOSIT_WIZARD,
  STANDARD_DEPOSIT_WIZARD,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import {
  productLocationTypes,
  productLocationTypesByName,
} from '../../util/enum/api/productTypes';
import {
  getCurrentAccount,
  getCurrentAccountUid,
} from '../../store/selectors/accountSelectors';
import {
  emptyProductTiers,
  fetchProductsWithPrices,
  fetchProductTiers,
} from '../../store/actions/products/productActions';
import { getPriceForLocation } from '../../store/actions/orders/orderActions';
import { getTriggerOrderQuote } from '../../store/actions/triggerOrders/triggerOrdersActions';
import { GET_TRIGGER_ORDER_QUOTE_LOADING } from '../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { selectLocationPrices } from '../../store/selectors/buyStorageSelectors';
import {
  selectProductPrices,
  selectProductTiers,
} from '../../store/selectors/productsSelector';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { selectBalancesAvailable } from '../../store/selectors/portfolioBalancesSelectors';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import {
  accountTypeKeys,
  accountTypes,
} from '../../util/enum/api/accountTypes';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  trackTriggerOrderDetailsEntered,
  trackGttBuyPriceTyped,
  trackGttBuyQuickSelectTapped,
  trackGttBuyQtyEntry,
  trackGttBuyQtyStepper,
  trackGttBuyVaultLocation,
  trackGttBuyInsufficientFunds,
  trackGttBuyAddFundsTapped,
  trackGttBuyCurrentPriceTapped,
} from './Common/gtmEventHelper';
import {
  GUTTER,
  AddFundsDimmer,
  ProductHeadingRow,
  ProductName,
  ProductPrice,
  FieldLabel,
  QuantityLabel,
  PriceInputWrapper,
  DollarSign,
  PriceInput,
  PriceError,
  PriceAndBelowMarketRow,
  PriceColumn,
  BelowMarketColumn,
  BelowMarketRow,
  BelowMarketButton,
  BelowMarketPrice,
  BelowMarketLabel,
  VaultSelectionWrapper,
  BasicDivider,
  VaultLabelText,
  VaultValue,
  VaultIcon,
  VaultNameText,
  BestPriceBanner,
  SegregatedTag,
  SelectCtaText,
  OrderEntryFooter,
  BuyAtCurrentPriceLink,
  ReviewButtonWrapper,
  ReviewTargetOrderButton,
  QuantityCalcWrapper,
  QuantityCalcTotal,
  QuantityCalcDivider,
  AvailableFundsRow,
  AvailableFundsRequired,
} from './Common';

// The wizard gutter at every width (BasicModalBody's own padding is scaled for
// older screens), with the frames' 10px under the header (as on Review).
const OrderEntryBody = styled(BasicModalBody)`
  padding: ${rem(10)} ${GUTTER} ${GUTTER};
`;

// Below-market quick-fill pills: exact percentages of the live market price.
const BELOW_MARKET_STEPS = [
  { percent: 5, factor: 0.95 },
  { percent: 10, factor: 0.9 },
];

// GET accounts/{accountUid}/trigger-orders/quotes' `side` param: Buy = -1,
// Sell = 1. Trigger orders only support Buy today.
const TRIGGER_ORDER_SIDE_BUY = -1;

// numeral() (formatMoneyNumeral's underlying formatter) silently returns
// "NaN" for values past roughly 1e21 — well beyond any real product price —
// so cap the integer part far below that instead of trying to format around
// it. 10 digits allows up to $9,999,999,999.99, orders of magnitude more
// than any realistic single-line total.
const MAX_PRICE_INTEGER_DIGITS = 10;

// Keep the price field to at most two decimals and a sane integer length;
// strip anything that isn't a digit or a single decimal point.
const sanitizePrice = (raw) => {
  let value = String(raw).replace(/[^\d.]/g, '');
  const firstDot = value.indexOf('.');
  if (firstDot !== -1) {
    const intPart = value.slice(0, firstDot);
    const decPart = value.slice(firstDot + 1).replace(/\./g, '');
    value = `${intPart.slice(0, MAX_PRICE_INTEGER_DIGITS)}.${decPart.slice(
      0,
      2,
    )}`;
  } else {
    value = value.slice(0, MAX_PRICE_INTEGER_DIGITS);
  }
  return value;
};

// Persists the entered price/quantity into WIZARD_DATA whenever they change, so
// a refresh (which reopens the modal on this step) restores them — matching how
// the vault selection already persists via setWizardData. Rendered inside the
// Formik <Form> so it sees every value source (typed price, below-market pills,
// quantity stepper). Reads live values; writes only once something is entered.
//
// Deliberately makes no network call: location prices are fetched when the user
// opens the vault picker (fetchVaultLocationPrices), not on every quantity or
// price change.
const PersistOrderValues = ({ values, onPersist }) => {
  useEffect(() => {
    if (values.CashAmount === '' && values.QuantityAmount === '') return;
    onPersist({
      CashAmount: values.CashAmount,
      QuantityAmount: values.QuantityAmount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.CashAmount, values.QuantityAmount]);

  return null;
};

PersistOrderValues.propTypes = {
  values: PropTypes.shape({
    CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onPersist: PropTypes.func.isRequired,
};

// Fires gtt_buy_insufficient_funds the moment the balance check transitions
// from passing to failing. Deliberately not fired from the submit click
// (handleReview): the Review/Add-Funds button is disabled outright when
// balanceLow is true and the account has no deposit-wizard permission at
// all (see the button's disabled expression below), so that click never
// happens for those users — firing from a click handler would silently
// never report the check failing for that entire segment. Watching the
// transition here fires exactly once per time the order goes over budget,
// regardless of whether the button is clickable.
const TrackInsufficientFunds = ({ isLow, onFail }) => {
  const wasLow = useRef(false);
  useEffect(() => {
    if (isLow && !wasLow.current) {
      onFail();
    }
    wasLow.current = isLow;
  }, [isLow, onFail]);

  return null;
};

TrackInsufficientFunds.propTypes = {
  isLow: PropTypes.bool.isRequired,
  onFail: PropTypes.func.isRequired,
};

// How often the vault's price tiers are refreshed while this step shows.
const PRICE_TIERS_REFRESH_MS = 30000;

// Second wizard step: set a target price below market, choose a quantity and a
// storage vault, then continue to review (no-op until built) or bail out to the
// regular Buy-at-current-price flow. Modeled on TargetPriceProductSelection for
// the wizard scaffolding and Buy/OrderSelection for the vault + pricing logic.
const TargetPriceOrderEntry = ({
  wizardData,
  setWizardData,
  setWizardTitle,
  setGoBackHandler,
  setCloseHandler,
  handleBack,
  handleNext,
  isLastStep,
  stepIndex,
  isGS,
  onSwitchToBuyFlow,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ecommerceGtmEvent } = useGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const locationPrices = useSelector(selectLocationPrices);
  const productPrices = useSelector(selectProductPrices);
  const productTiers = useSelector(selectProductTiers);
  const settings = useSelector(selectSettings);
  const balance = useSelector(selectBalancesAvailable);
  const availableForTrading = balance?.AvailableForTrading;
  const isQuoteLoading = useSelector(
    selectIsLoadingByActionTypes([GET_TRIGGER_ORDER_QUOTE_LOADING]),
  );

  const {
    displayStandardOptionsPermission,
    displayIRAEquityOptionsPermission,
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
  } = useCheckPermissions();

  const [openSelectVaultLocation, setOpenSelectVaultLocation] = useState(false);
  const [openSelectStorageType, setOpenSelectStorageType] = useState(false);
  const [isVaultPricesLoading, setIsVaultPricesLoading] = useState(false);
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  // Previous open state, so the session-repair effect below fires only on the
  // close transition rather than on mount.
  const wasAddFundsOpen = useRef(false);
  // Set on an actual keystroke in the price/quantity fields, cleared after
  // firing on blur (or whenever not set). Session-restored values and the
  // below-market pills/stepper buttons set the field through setFieldValue
  // directly, never through these onChange handlers, so blurring a field that
  // was only ever populated that way does not fire gtt_buy_price_typed/
  // gtt_buy_qty_entry — only a blur that follows a real typed edit does.
  const didTypePriceRef = useRef(false);
  const didTypeQuantityRef = useRef(false);

  // The four deposit wizards are permission-gated and mutually exclusive. With
  // none of them available there is nothing to open, so don't hand the user a
  // relabelled button that does nothing when clicked.
  const canAddFunds =
    displayStandardOptionsPermission ||
    displayIRAEquityOptionsPermission ||
    displayIRAEntrustOptionsPermission ||
    displayIRAMilleniumOptionsPermission;

  const selectedPlan = wizardData?.selectedPlan || 'standard';

  const product = wizardData?.selectedProduct;
  const locations = product?.Locations || [];
  const productCode = product?.ProductCode || product?.Code;
  const productName = product?.Caption;

  // Instavault (fractional) products have exactly one location and nothing
  // to choose between — mirror ProductCard/Buy's isInstavault check and hide
  // the vault row entirely instead of showing a picker with a single option.
  const isInstavaultProduct =
    isGS &&
    (productCode === settings?.InstavaultProductCodes?.[0] ||
      productCode === settings?.InstavaultProductCodes?.[1]);

  const isIRAAccount =
    parseEnumType(accountTypeKeys, account?.AccountType) === accountTypes.IRA;

  // Default/lowest vault, matching Buy: cheapest non-delivery (LocationType 12)
  // location.
  const minPrice = locations.length
    ? Math.min(...locations.map((l) => l.PricePerUnit))
    : 0;
  const firstLowestVault = locations.find(
    (l) => l.PricePerUnit === minPrice && l.LocationType !== 12,
  );

  // A selectedVault carried over in wizardData from a previously-selected
  // product (e.g. the user went back to product selection and picked a
  // different product) can reference a SymbolCode that doesn't exist on the
  // current product's locations. Confirm it still resolves here — otherwise
  // useProductPrice can't find a matching location and returns undefined,
  // leaving the below-market price pills blank/disabled.
  const isValidVaultSelected = () =>
    !!wizardData?.selectedVault &&
    wizardData.selectedVault.LocationType !== 12 &&
    locations.some(
      (location) => location.SymbolCode === wizardData.selectedVault.SymbolCode,
    );

  const priceLocation = isValidVaultSelected()
    ? wizardData.selectedVault
    : firstLowestVault;
  const livePrice = useProductPrice(product, priceLocation, false, true);

  // Order total (target price × quantity), from the values PersistOrderValues
  // keeps in the wizard data, so SelectVaultLocation (outside the Formik) can
  // match it against the vault's price tiers.
  const orderTotal =
    Number(wizardData?.orderValues?.QuantityAmount || 0) *
    parseFloat(wizardData?.orderValues?.CashAmount || 0);

  // The vault whose price tiers set the price: the one picked in the vault
  // picker or, for an Instavault product (which has no picker), its only
  // location. As in Buy, its tiers are fetched as soon as the step opens (and
  // whenever the vault changes) and refreshed every 30 seconds while it shows.
  const tierVault =
    isValidVaultSelected() || isInstavaultProduct ? priceLocation : undefined;
  const tierSymbol = tierVault?.SymbolCode;
  const tierLocation = Object.keys(productLocationTypesByName).find(
    (key) => productLocationTypesByName[key] === tierVault?.LocationType,
  );

  // Each open starts from the product price: tiers left in the store (by Buy,
  // the product page, or a response that landed after the flow closed) must not
  // show until this step's own fetch replaces them, and none are left behind
  // when it closes. Runs before the fetch below.
  useEffect(() => {
    dispatch(emptyProductTiers());

    return () => dispatch(emptyProductTiers());
  }, [dispatch]);

  useEffect(() => {
    if (accountUid && tierSymbol && tierLocation) {
      dispatch(
        fetchProductTiers({
          accountUid,
          symbol: tierSymbol,
          side: 'Buy',
          location: tierLocation,
          trueTiers: true,
        }),
      );
    }
  }, [accountUid, dispatch, tierSymbol, tierLocation]);

  useGetPeriodicPriceTiers(
    'gttPriceTiers',
    account,
    tierLocation,
    tierSymbol,
    'Buy',
    dispatch,
    !!tierSymbol,
    PRICE_TIERS_REFRESH_MS,
    true,
  );

  // "Current Price" shown in the header and used for the below-market-price
  // validation. Without a vault (and not Instavault) it is the product's own
  // price (livePrice): the tiers and location prices below live in global state
  // and can be left over from an earlier flow for the same product. Otherwise it
  // prefers the PricePerUnit of the vault's price tier that the order total
  // falls in. For a vault the user picked, accounts/{accountUid}/products/
  // location/prices — which accepts an optional targetPrice and returns each
  // vault's tier-selected LocationPrice computed server-side — is read next, for
  // whatever it last returned for that vault (refreshed each time the vault
  // drawer is opened). Then the flat livePrice until a priced response arrives.
  const vaultTierPrice = tierVault
    ? getVaultTierPrice(productTiers, tierVault, orderTotal)
    : undefined;
  const pickedVaultLocationPrice = isValidVaultSelected()
    ? locationPrices.find((location) => location.SymbolCode === tierSymbol)
        ?.LocationPrice
    : undefined;
  const displayPrice = vaultTierPrice ?? pickedVaultLocationPrice ?? livePrice;

  // Formatted tier-range label (e.g. "$50k–$55k" or "$1M+") for analytics on
  // the qty-entry/qty-stepper events — null for the base (no-discount) tier
  // or when tiers aren't loaded yet. Mirrors TargetPriceOrderReview's
  // volumeDiscountRange, computed here from the live order total (no quote
  // exists yet at this step, unlike Review's BE-sourced quote.LowBound/
  // HighBound).
  const volumeTierLabel = (orderValueTotal) => {
    const tier = getTierForAmount(productTiers?.Tiers, orderValueTotal);
    if (!tier || !(Number(tier.LowBound) > 0)) return null;
    const formatBound = (bound) =>
      formatMoneyNumeral(bound, '$0a').replace(/m$/, 'M');
    return tier.HighBound != null
      ? `${formatBound(tier.LowBound)}–${formatBound(tier.HighBound)}`
      : `${formatBound(tier.LowBound)}+`;
  };

  // Shared by the price input's onBlur and handleReview below. Enter in a
  // Formik <Form> submits directly rather than blurring first, and the quote
  // fetch that follows (not synchronous) can unmount this step before the
  // blur that would normally fire ever reaches React — losing the typed
  // event entirely for a user who types then hits Enter instead of clicking
  // Review or tabbing away. Checking the same ref at the top of handleReview
  // catches that path without double-firing the normal blur path: a click on
  // the Review button blurs the input first (clearing the ref) before the
  // click's own submit handler runs.
  const firePriceTypedEvent = (cashAmount) => {
    if (cashAmount === '') return;
    trackGttBuyPriceTyped({
      ecommerceGtmEvent,
      productId: productCode,
      triggerPrice: parseFloat(cashAmount),
      currentPrice: Number(displayPrice) || null,
    });
  };

  const fireQuantityTypedEvent = (cashAmount, quantityAmount) => {
    const quantity = Number(quantityAmount) || 0;
    if (quantity < 1) return;
    const orderValue = quantity * (parseFloat(cashAmount) || 0);
    trackGttBuyQtyEntry({
      ecommerceGtmEvent,
      productId: productCode,
      productName,
      quantity,
      orderValue,
      volumeTier: volumeTierLabel(orderValue),
    });
  };

  // Set the header title and route the back chevron to the previous step. Load
  // buy-side product prices so useProductPrice has data even on a
  // session-restored deep entry into this step.
  useEffect(() => {
    setWizardTitle(t('gtt.common.buyAtTargetPrice'));
    setGoBackHandler(() => handleBack);
    return () => setGoBackHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only fetch when prices aren't already loaded. When entering from a product
  // card (or after product selection) prices are already in the store, and
  // re-dispatching flips the global PRODUCT_PRICES_LOADING flag — which the buy
  // listing uses to swap the whole product list (and this modal, when it is
  // hosted by a ProductCard) for a loader, tearing the flow down on open. Fetch
  // only covers a session-restored deep entry with no prices yet.
  useEffect(() => {
    if (accountUid && !productPrices?.length) {
      dispatch(fetchProductsWithPrices({ accountUid, side: 'Buy' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, dispatch]);

  // Available-funds figure shown under the quantity stepper; the GTT flow may be
  // entered directly (deep-link/hand-off) without the balances already loaded.
  useEffect(() => {
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
  }, [accountUid, dispatch]);

  // Trigger Orders support vault purchases only — delivery (LocationType 12) is
  // not a supported fulfillment option, so exclude it from the vault picker.
  // The default/valid-vault logic already excludes it; this guarantees it never
  // appears as a selectable option either.
  const sortedLocations = [...locationPrices]
    .filter((location) => location.LocationType !== 12)
    .sort((a, b) => a.LocationPrice - b.LocationPrice);

  // Called from the vault-row click (opens the drawer). targetPrice is omitted
  // when not a positive number — axios drops undefined params, so this call is
  // identical to the standard Buy flow's own (targetPrice-less) use of this
  // same endpoint.
  const fetchLocationPrices = (quantity, targetPrice, callbacks = {}) => {
    if (!accountUid || !product) return;

    dispatch(
      getPriceForLocation({
        accountUid,
        data: {
          productCode: product.ProductCode,
          quantityOrAmount: quantity || 1,
          unitType: 'Quantity',
          paymentMethodType: 'AccountBalance',
          targetPrice:
            Number(targetPrice) > 0 ? Number(targetPrice) : undefined,
        },
        ...callbacks,
      }),
    );
  };

  const fetchVaultLocationPrices = (quantity, targetPrice) => {
    setOpenSelectVaultLocation(true);
    setIsVaultPricesLoading(true);
    fetchLocationPrices(quantity, targetPrice, {
      onSuccess: () => setIsVaultPricesLoading(false),
      onError: () => setIsVaultPricesLoading(false),
      isLoading: setIsVaultPricesLoading,
    });
  };

  const selectedVaultName = () =>
    startCase(
      parseEnumType(
        productLocationTypes,
        wizardData.selectedVault.LocationType,
      ),
    );

  // "Best Price" reflects the vault's standard-storage rate — Segregated adds
  // a storage premium, so the claim no longer holds once it's selected.
  // Mirrors Buy/OrderSelection's vaultTag(), which excludes it the same way.
  const isLowestVaultSelected = () =>
    isValidVaultSelected() &&
    firstLowestVault &&
    wizardData.selectedVault.LocationType === firstLowestVault.LocationType &&
    selectedPlan !== 'premium';

  const isSegregatedVaultSelected = () =>
    isValidVaultSelected() && selectedPlan === 'premium';

  const vaultRowValue = (
    <VaultValue>
      {isValidVaultSelected() && <VaultIcon />}
      <VaultNameText isPlaceholder={!isValidVaultSelected()}>
        {isValidVaultSelected()
          ? selectedVaultName()
          : t('gtt.triggerOrders.orderEntry.selectVaultPlaceholder')}
      </VaultNameText>
      {isSegregatedVaultSelected() ? (
        <SegregatedTag>
          {settings?.SegregatedCaption || t('buyWizard.premium')}
        </SegregatedTag>
      ) : (
        isLowestVaultSelected() && (
          <BestPriceBanner>{t('buyWizard.bestPrice')}</BestPriceBanner>
        )
      )}
    </VaultValue>
  );

  const validate = (values) => {
    const errors = {};
    if (
      values.CashAmount !== '' &&
      Number(displayPrice) > 0 &&
      parseFloat(values.CashAmount) >= Number(displayPrice)
    ) {
      errors.CashAmount = t('gtt.errors.priceMustBeLessThanCurrentPrice', {
        livePrice: formatMoneyNumeral(displayPrice),
      });
    }
    return errors;
  };

  // The amount that would be reserved for this order (target price × quantity,
  // same figure as the Review screen's totalAtExecution/ReservedFundsBanner)
  // exceeds what's available. Balances load asynchronously, so skip the check
  // (rather than false-flagging as low) until availableForTrading is in.
  const isBalanceLow = (values) => {
    const hasPrice =
      values.CashAmount !== '' && parseFloat(values.CashAmount) > 0;
    const hasQuantity = Number(values.QuantityAmount) >= 1;
    if (!hasPrice || !hasQuantity || availableForTrading === undefined) {
      return false;
    }
    const reservedAmount =
      parseFloat(values.CashAmount) * Number(values.QuantityAmount);
    return reservedAmount > Number(availableForTrading);
  };

  // How much the order overshoots the balance by. Only meaningful once
  // isBalanceLow is true — that's what guarantees the operands are numbers —
  // so every caller reads it from inside a low-balance branch.
  const getShortfall = (values) =>
    parseFloat(values.CashAmount) * Number(values.QuantityAmount) -
    Number(availableForTrading);

  const isReviewDisabled = (values) => {
    const hasPrice =
      values.CashAmount !== '' && parseFloat(values.CashAmount) > 0;
    const hasQuantity = Number(values.QuantityAmount) >= 1;
    // No price or no quantity entered → always disabled, regardless of vault.
    if (!hasPrice || !hasQuantity) return true;
    // Not enough funds to cover the order → keep disabled rather than
    // advancing to Review; the button relabels to "Add Funds" instead.
    if (isBalanceLow(values)) return true;
    const priceBelowMarket =
      Number(displayPrice) > 0 &&
      parseFloat(values.CashAmount) < Number(displayPrice);
    // Instavault products have no vault picker to satisfy — the single
    // location resolved into priceLocation/firstLowestVault is implicit.
    return !(
      priceBelowMarket &&
      (isValidVaultSelected() || isInstavaultProduct)
    );
  };

  // The deposit wizards report only that they closed, never that a deposit
  // completed, and the balances saga takes no onSuccess — so refetch
  // unconditionally and let isBalanceLow (and with it the button label)
  // re-evaluate against whatever the new balance turns out to be. Same shape
  // as Buy's SelectPaymentOptions after a payment method is added.
  const handleAddFundsClose = () => {
    setIsAddFundsOpen(false);
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
  };

  // Closing a deposit wizard tears down session state this flow shares with
  // it: Modal clears the whole WIZARD object (avoidClearingStorage is
  // commented out in StandardDepositWizard, so it can't opt out), taking this
  // flow's step index with it, and IraDepositWizard removes WIZARD_DATA
  // regardless of overModal. Neither is visible straight away — GTTFlow holds
  // wizardData in React state — but a refresh afterwards would reopen the flow
  // at product selection with the product gone. Re-persist on the close
  // transition, from an effect so it lands after the wizard's own close
  // handlers instead of being overwritten by them.
  useEffect(() => {
    if (wasAddFundsOpen.current && !isAddFundsOpen) {
      storeInSessionStorage(WIZARD_DATA, wizardData);
      setWizardContent(TRIGGER_ORDER_WIZARD, stepIndex);
      // Drop the deposit wizard's own step keys so a second "Add Funds" click
      // starts at its first screen rather than where the user left off.
      deleteWizardKey(STANDARD_DEPOSIT_WIZARD);
      deleteWizardKey(IRA_DEPOSIT_WIZARD);
      deleteWizardKey(ACH_DEPOSIT_WIZARD);
    }
    wasAddFundsOpen.current = isAddFundsOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddFundsOpen]);

  // The dimmer covers the modal body but not GTT's header, so its close X
  // stays clickable while the deposit wizard is up — and closing the flow
  // there would unmount the deposit wizard from under the user. Point the X at
  // the deposit wizard instead so it closes the top-most layer, and hand it
  // back when that layer is gone.
  useEffect(() => {
    if (!isAddFundsOpen) return undefined;
    setCloseHandler(() => handleAddFundsClose);
    // Hand the X back on close, and on unmount so the flow can never be left
    // pointing at this screen's handler (same shape as setGoBackHandler above).
    return () => setCloseHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddFundsOpen]);

  const handleReview = (values) => {
    // Enter in either field submits the form directly rather than blurring
    // first (see firePriceTypedEvent's comment above), so catch any pending
    // typed-but-not-yet-blurred value here too — this is a no-op for the
    // normal click/tab-away path, since blur there already cleared the ref.
    if (didTypePriceRef.current) {
      didTypePriceRef.current = false;
      firePriceTypedEvent(values.CashAmount);
    }
    if (didTypeQuantityRef.current) {
      didTypeQuantityRef.current = false;
      fireQuantityTypedEvent(values.CashAmount, values.QuantityAmount);
    }

    // Not enough funds to cover the order: the button reads "Add Funds", so
    // open the deposit wizard over the flow instead of advancing to review.
    // Price/quantity stay put — the wizard is a sibling, the Form never
    // unmounts — and handleAddFundsClose refetches the balance on the way out.
    if (isBalanceLow(values)) {
      if (!canAddFunds) return;
      // gtt_buy_add_funds_tapped — the user clicking this CTA. (The check
      // actually failing is reported separately by TrackInsufficientFunds
      // below, since this button is disabled entirely — never reaching this
      // handler — when canAddFunds is false.) Supersedes the older
      // trigger_order_add_funds_clicked (dropped — same moment, different
      // key names, no remaining consumer).
      trackGttBuyAddFundsTapped({
        ecommerceGtmEvent,
        productId: productCode,
        productName,
        orderValue:
          parseFloat(values.CashAmount) * Number(values.QuantityAmount),
        shortfallAmount: getShortfall(values),
      });
      setIsAddFundsOpen(true);
      return;
    }

    // Fetch a quote for the Review screen (authoritative Subtotal/
    // TargetPriceSavings/TotalAtExecution + the QuoteUid the Review screen's
    // submit executes against). Only advance once the quote comes back.
    dispatch(
      getTriggerOrderQuote({
        accountUid,
        symbolCode: priceLocation?.SymbolCode,
        side: TRIGGER_ORDER_SIDE_BUY,
        quantity: Number(values.QuantityAmount),
        targetPrice: parseFloat(values.CashAmount),
        priceAtSubmission: Number(displayPrice),
        isSegregated: selectedPlan === 'premium',
        onSuccess: (quote) => {
          setWizardData({
            orderValues: {
              CashAmount: values.CashAmount,
              QuantityAmount: values.QuantityAmount,
            },
            activeType: 'quantity',
            quote,
            // Carry this screen's displayed (tier-aware) price forward so
            // Review shows the same current price the user saw here,
            // instead of re-fetching (and potentially showing a different)
            // price.
            productPrice: Number(displayPrice),
          });
          trackTriggerOrderDetailsEntered({
            ecommerceGtmEvent,
            productCode: product?.ProductCode || product?.Code,
            targetPrice: values.CashAmount,
            quantity: values.QuantityAmount,
            vault: isValidVaultSelected() ? selectedVaultName() : undefined,
          });
          // Review/Confirmation screens aren't built yet, so don't advance
          // off the last defined step — that would blank the modal (mirrors
          // ProductSelection).
          if (!isLastStep) {
            handleNext();
          }
        },
      }),
    );
  };

  const handleSwitchToBuy = (values) => {
    // entryPoint is this GTT journey's own entry point, read directly — the
    // switch-over to Buy (which stashes it under gttEntryPoint for a possible
    // switch back) hasn't happened yet at the point this fires.
    trackGttBuyCurrentPriceTapped({
      ecommerceGtmEvent,
      productId: productCode,
      productName,
      currentPrice: Number(displayPrice) || null,
      entryPoint: wizardData?.entryPoint,
    });
    onSwitchToBuyFlow(
      product,
      wizardData?.selectedVault,
      values.QuantityAmount,
    );
  };

  return (
    <>
      <SectionLoader isLoading={isQuoteLoading}>
        <OrderEntryBody>
          <ProductHeadingRow>
            <ProductName>{product?.ShortCaption}</ProductName>
            <ProductPrice>{formatMoneyNumeral(displayPrice)}</ProductPrice>
          </ProductHeadingRow>

          <Formik
            initialValues={{
              // Prefer the dedicated GTT_TARGET_PRICE/GTT_TARGET_QUANTITY (they
              // survive a Buy round trip, and a step remount, that can clear
              // orderValues); fall back to orderValues. Re-sanitize on the way
              // in too — a value stored before sanitizePrice existed (or
              // before its integer-length cap was added) would otherwise be
              // restored as-is, bypassing the cap entirely.
              CashAmount: sanitizePrice(
                retrieveFromSessionStorage(GTT_TARGET_PRICE) ||
                  wizardData?.orderValues?.CashAmount ||
                  '',
              ),
              QuantityAmount:
                retrieveFromSessionStorage(GTT_TARGET_QUANTITY) ||
                wizardData?.orderValues?.QuantityAmount ||
                '',
            }}
            enableReinitialize
            validate={validate}
            validateOnBlur={false}
            onSubmit={handleReview}
          >
            {({ values, setFieldValue }) => {
              const balanceLow = isBalanceLow(values);
              // Derived from the live values rather than Formik's `errors`
              // state: persisting a quantity change reinitializes the form
              // (enableReinitialize), which clears `errors` and would drop the
              // message while the price is still above the current price.
              const errors = validate(values);
              return (
                <Form>
                  <PersistOrderValues
                    values={values}
                    onPersist={(orderValues) => {
                      setWizardData({ orderValues });
                      // Mirror price/quantity into their own keys so they survive
                      // a Buy round trip or a step remount that can overwrite or
                      // transiently clear orderValues.
                      storeInSessionStorage(
                        GTT_TARGET_PRICE,
                        orderValues.CashAmount,
                      );
                      storeInSessionStorage(
                        GTT_TARGET_QUANTITY,
                        orderValues.QuantityAmount,
                      );
                    }}
                  />
                  <TrackInsufficientFunds
                    isLow={balanceLow}
                    onFail={() => {
                      trackGttBuyInsufficientFunds({
                        ecommerceGtmEvent,
                        productId: productCode,
                        productName,
                        orderValue:
                          parseFloat(values.CashAmount) *
                          Number(values.QuantityAmount),
                        availableBalance: Number(availableForTrading),
                        shortfallAmount: getShortfall(values),
                      });
                    }}
                  />
                  <PriceAndBelowMarketRow>
                    <PriceColumn>
                      <FieldLabel>
                        {t('gtt.triggerOrders.orderEntry.setPrice')}*
                      </FieldLabel>
                      <PriceInputWrapper hasError={!!errors.CashAmount}>
                        <DollarSign>$</DollarSign>
                        <PriceInput
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          value={values.CashAmount}
                          onChange={(e) => {
                            setFieldValue(
                              'CashAmount',
                              sanitizePrice(e.target.value),
                            );
                            didTypePriceRef.current = true;
                          }}
                          onBlur={() => {
                            // gtt_buy_price_typed — fired on blur, but only
                            // when this blur follows an actual keystroke here
                            // (didTypePriceRef). A field blurred after being
                            // restored from session storage or filled by a
                            // below-market pill was never typed into and
                            // shouldn't report as typed.
                            if (!didTypePriceRef.current) return;
                            didTypePriceRef.current = false;
                            firePriceTypedEvent(values.CashAmount);
                          }}
                          data-cy="gtt-target-price"
                        />
                      </PriceInputWrapper>
                      {errors.CashAmount && (
                        <PriceError>{errors.CashAmount}</PriceError>
                      )}
                    </PriceColumn>

                    <BelowMarketColumn>
                      <FieldLabel>
                        {t('gtt.triggerOrders.orderEntry.recommendedSelection')}
                      </FieldLabel>
                      <BelowMarketRow>
                        {BELOW_MARKET_STEPS.map(({ percent, factor }) => {
                          const belowMarketPrice = (
                            Number(displayPrice) * factor
                          ).toFixed(2);
                          const isSelected =
                            values.CashAmount !== '' &&
                            values.CashAmount === belowMarketPrice;
                          return (
                            <BelowMarketButton
                              key={percent}
                              type="button"
                              isSelected={isSelected}
                              disabled={!Number(displayPrice)}
                              onClick={() => {
                                setFieldValue('CashAmount', belowMarketPrice);
                                trackGttBuyQuickSelectTapped({
                                  ecommerceGtmEvent,
                                  productId: productCode,
                                  productName,
                                  quickSelectPct: percent,
                                  triggerPricePopulated: Number(
                                    belowMarketPrice,
                                  ),
                                  currentPrice: Number(displayPrice) || null,
                                });
                              }}
                              data-cy={`gtt-below-market-${percent}`}
                            >
                              <BelowMarketPrice>
                                {formatMoneyNumeral(
                                  Number(displayPrice) * factor,
                                )}
                              </BelowMarketPrice>
                              <BelowMarketLabel>
                                {t('gtt.triggerOrders.orderEntry.belowMarket', {
                                  percent,
                                })}
                              </BelowMarketLabel>
                            </BelowMarketButton>
                          );
                        })}
                      </BelowMarketRow>
                    </BelowMarketColumn>
                  </PriceAndBelowMarketRow>

                  <QuantityLabel>
                    {t('gtt.triggerOrders.orderEntry.enterQuantity')}*
                  </QuantityLabel>
                  <QuantityStepper
                    value={values.QuantityAmount}
                    onChange={(next, method) => {
                      setFieldValue('QuantityAmount', next);
                      // Stepper clicks are each a discrete, complete action,
                      // so gtt_buy_qty_stepper fires immediately. Typed
                      // entry is a keystroke stream — firing per keystroke
                      // here would log one event per digit (typing "250"
                      // would report quantities 2, 25 and 250), so that path
                      // only flags didTypeQuantityRef and waits for onBlur
                      // (below) to fire gtt_buy_qty_entry once.
                      if (method !== 'stepper') {
                        didTypeQuantityRef.current = true;
                        return;
                      }
                      const quantity = Number(next) || 0;
                      // No event for clearing the quantity back to empty —
                      // AC covers setting a quantity, not clearing one.
                      if (quantity < 1) return;
                      const orderValue =
                        quantity * (parseFloat(values.CashAmount) || 0);
                      trackGttBuyQtyStepper({
                        ecommerceGtmEvent,
                        productId: productCode,
                        productName,
                        quantity,
                        orderValue,
                        volumeTier: volumeTierLabel(orderValue),
                      });
                    }}
                    onBlur={() => {
                      if (!didTypeQuantityRef.current) return;
                      didTypeQuantityRef.current = false;
                      fireQuantityTypedEvent(
                        values.CashAmount,
                        values.QuantityAmount,
                      );
                    }}
                  />

                  {/* Quantity total (at the entered target price) + available
                    funds, mirroring the Buy flow's calculated-values block. */}
                  <QuantityCalcWrapper>
                    {Number(values.QuantityAmount) >= 1 && (
                      <>
                        <QuantityCalcTotal>
                          {Number(values.QuantityAmount)}{' '}
                          {Number(values.QuantityAmount) > 1 ? 'Units' : 'Unit'}{' '}
                          ={' '}
                          <b>
                            {formatMoneyNumeral(
                              Number(values.QuantityAmount) *
                                parseFloat(values.CashAmount || 0),
                            )}
                          </b>
                        </QuantityCalcTotal>
                        <QuantityCalcDivider />
                        <AvailableFundsRow>
                          {t('gtt.triggerOrders.orderEntry.availableFunds')} :{' '}
                          <b>{formatMoneyNumeral(availableForTrading)}</b>
                        </AvailableFundsRow>
                        {balanceLow && (
                          <AvailableFundsRequired data-cy="gtt-amount-required">
                            {t('gtt.triggerOrders.orderEntry.amountRequired', {
                              amount: formatMoneyNumeral(getShortfall(values)),
                            })}
                          </AvailableFundsRequired>
                        )}
                      </>
                    )}
                  </QuantityCalcWrapper>

                  {/* Instavault products have no vault to choose, so the row
                    is hidden — OrderEntryFooter's pinToBottom then takes over
                    the margin-top:auto this row would otherwise carry, so the
                    footer still lands at the bottom instead of floating right
                    under the quantity block. */}
                  {!isInstavaultProduct && (
                    <VaultSelectionWrapper>
                      <BasicDivider />
                      <RowItemElement
                        label={
                          <VaultLabelText>
                            {t('gtt.triggerOrders.orderEntry.vault')}
                          </VaultLabelText>
                        }
                        value={vaultRowValue}
                        clickLabel={
                          <SelectCtaText>
                            {isValidVaultSelected()
                              ? t('buyWizard.changeLabel')
                              : t('buyWizard.selectLabel')}
                          </SelectCtaText>
                        }
                        onClick={() =>
                          fetchVaultLocationPrices(
                            Number(values.QuantityAmount),
                            values.CashAmount,
                          )
                        }
                        dataCy="gtt-vault-row"
                      />
                    </VaultSelectionWrapper>
                  )}

                  <OrderEntryFooter pinToBottom={isInstavaultProduct}>
                    <BuyAtCurrentPriceLink
                      type="button"
                      onClick={() => handleSwitchToBuy(values)}
                      data-cy="gtt-buy-at-current-price"
                    >
                      <span>
                        {t(
                          'gtt.triggerOrders.orderEntry.buyAtCurrentPriceLine1',
                        )}
                      </span>
                      <span>
                        {t(
                          'gtt.triggerOrders.orderEntry.buyAtCurrentPriceLine2',
                        )}
                      </span>
                    </BuyAtCurrentPriceLink>
                    <ReviewButtonWrapper>
                      <ReviewTargetOrderButton
                        size="lg"
                        variant="primary"
                        type="submit"
                        textTransform="none"
                        disabled={
                          (isReviewDisabled(values) &&
                            !(balanceLow && canAddFunds)) ||
                          isQuoteLoading ||
                          !!errors.CashAmount
                        }
                        data-cy="gtt-review-target-order"
                      >
                        {balanceLow
                          ? t('gtt.triggerOrders.orderEntry.addFunds')
                          : t('gtt.triggerOrders.orderEntry.reviewTargetOrder')}
                      </ReviewTargetOrderButton>
                    </ReviewButtonWrapper>
                  </OrderEntryFooter>
                </Form>
              );
            }}
          </Formik>
        </OrderEntryBody>
      </SectionLoader>

      {/* Reuse Buy's vault picker + storage-type drawer for parity. Each
          vault's LocationPrice already reflects the targetPrice-aware tier
          (fetchVaultLocationPrices passes targetPrice on open), so no extra
          props are needed here beyond the standard Buy wiring. */}
      <SelectVaultLocation
        open={openSelectVaultLocation}
        setOpen={setOpenSelectVaultLocation}
        wizardData={wizardData}
        setWizardData={setWizardData}
        lowestVault={firstLowestVault}
        sortedLocations={sortedLocations}
        locations={locations}
        isLoading={isVaultPricesLoading}
        isIRAAccount={isIRAAccount}
        setOpenSelectStorageType={setOpenSelectStorageType}
        isTargetPriceOrder
        orderTotal={orderTotal}
        onVaultSelected={(vault) =>
          trackGttBuyVaultLocation({
            ecommerceGtmEvent,
            productId: productCode,
            productName,
            vaultLocation: startCase(
              parseEnumType(productLocationTypes, vault.LocationType),
            ),
          })
        }
      />
      <SelectStorageType
        open={openSelectStorageType}
        setOpen={setOpenSelectStorageType}
        wizardData={wizardData}
        setWizardData={setWizardData}
        setOpenSelectVaultLocation={setOpenSelectVaultLocation}
        selectedPlan={selectedPlan}
        segregatedCaption={settings?.SegregatedCaption}
        isGS={isGS}
      />

      {/* Add Funds, opened from the relabelled footer button when the order
          costs more than the available balance. Rendered outside SectionLoader
          for the same reason as the drawers above: the dimmer is
          position:absolute, and LoaderContainer is a scrolling positioned box,
          so anchoring to the modal card means it covers the whole body.

          overModal is required, not decorative: it keeps StandardDepositWizard
          from clearing this flow's WIZARD_DATA on close, and lifts the wizard's
          portal above GTT's own modal layer. The four branches are
          permission-gated and mutually exclusive, mirroring Buy/OrderSelection. */}
      {isAddFundsOpen && <AddFundsDimmer />}

      {isAddFundsOpen && displayStandardOptionsPermission && (
        <StandardDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          overModal
        />
      )}
      {isAddFundsOpen && displayIRAEquityOptionsPermission && (
        <IraDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          type={IRA_EQUITY_TYPE}
          overModal
        />
      )}
      {isAddFundsOpen && displayIRAEntrustOptionsPermission && (
        <IraDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          type={IRA_ENTRUST_TYPE}
          overModal
        />
      )}
      {isAddFundsOpen && displayIRAMilleniumOptionsPermission && (
        <MilleniumDepositWizard
          isModalOpen={isAddFundsOpen}
          handleClose={handleAddFundsClose}
          overModal
        />
      )}
    </>
  );
};

TargetPriceOrderEntry.propTypes = {
  wizardData: PropTypes.shape({
    selectedProduct: PropTypes.shape({
      ProductCode: PropTypes.string,
      Code: PropTypes.string,
      Caption: PropTypes.string,
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          SymbolCode: PropTypes.string,
          LocationType: PropTypes.number,
          PricePerUnit: PropTypes.number,
        }),
      ),
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    selectedPlan: PropTypes.string,
    entryPoint: PropTypes.string,
  }),
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  // Overrides the modal's close X while the deposit wizard is open, so it
  // closes that layer instead of tearing down the whole flow.
  setCloseHandler: PropTypes.func,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  isLastStep: PropTypes.bool,
  // This screen's index in the wizard, re-persisted after a deposit wizard
  // clears the shared WIZARD session object on close.
  stepIndex: PropTypes.number,
  isGS: PropTypes.bool,
  onSwitchToBuyFlow: PropTypes.func,
};

export default TargetPriceOrderEntry;
